// src/SparepartsPages/Dpo/DpoDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Chip,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* helpers */
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const daysAgoISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
// backend returns YYYY-MM-DD strings -> no timezone issue
const fmt = (v) => (v ? String(v).slice(0, 10) : "");
const num = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);

const StatusChip = ({ v }) => {
  if (!v) return <Chip size="small" label="-" />;
  const color =
    v === "MATCHED_OK"
      ? "success"
      : v === "SHORT_RECEIVE" || v === "PENDING_RECEIVE"
      ? "warning"
      : v === "EXCESS_RECEIVE"
      ? "info"
      : v === "NO_INVNO"
      ? "default"
      : "secondary";
  return <Chip size="small" color={color} label={v} />;
};

const DpoDashboard = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const isLoggedIn = !!userToken;

  // keep your session behaviour
  setTimeout(() => window.location.reload(false), 1800000);

  /* party autocomplete */
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyQ, setPartyQ] = useState("");
  const [party, setParty] = useState(null);

  /* filters */
  const [dateMode, setDateMode] = useState("transit"); // transit|dpo|case
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [status, setStatus] = useState("ALL");
  const [purchased, setPurchased] = useState("ALL"); // ALL|YES|NO
  const [search, setSearch] = useState("");
  const [showNoInv, setShowNoInv] = useState("YES"); // YES|NO

  /* grid */
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // IMPORTANT: backend allowSort expects keys like "last_activity"
  const [sortModel, setSortModel] = useState([{ field: "last_activity", sort: "desc" }]);

  /* drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [activeRow, setActiveRow] = useState(null);

  const [itemsLoading, setItemsLoading] = useState(false);
  const [items, setItems] = useState([]);

  const [casesLoading, setCasesLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [rackMissing, setRackMissing] = useState(false);

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        sptoken: userToken,
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Request failed");
    return data;
  };

  /* party load */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isLoggedIn) return;
      if (!partyOpen) return;

      setPartyLoading(true);
      try {
        const data = await fetchJSON(`/dpo/partylist?q=${encodeURIComponent(partyQ)}`);
        if (alive) setPartyOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        if (alive) setPartyLoading(false);
      }
    };
    run();
    return () => (alive = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyOpen, partyQ, isLoggedIn]);

  const baseQS = useMemo(() => {
    const p = new URLSearchParams();
    p.set("dateMode", dateMode);
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    p.set("status", status);
    p.set("purchased", purchased);
    p.set("showNoInv", showNoInv);
    if (search) p.set("search", search);
    if (party?.subledgerid) p.set("partyid", String(party.subledgerid));
    return p.toString();
  }, [dateMode, from, to, status, purchased, search, party, showNoInv]);

  const loadGrid = useCallback(async () => {
    setGridLoading(true);
    try {
      const sm = sortModel?.[0] || { field: "last_activity", sort: "desc" };
      const p = new URLSearchParams(baseQS);
      p.set("page", page);
      p.set("pageSize", pageSize);
      p.set("sortBy", sm.field || "last_activity");
      p.set("sortDir", sm.sort || "desc");

      const data = await fetchJSON(`/dpo/dashboard/invoices?${p.toString()}`);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setRowCount(Number(data.rowCount || 0));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGridLoading(false);
    }
  }, [baseQS, page, pageSize, sortModel]);

  const apply = async () => {
    setPage(0);
    await loadGrid();
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    loadGrid();
  }, [loadGrid, isLoggedIn]);

  const openRow = async (row) => {
    if (!row) return;

    // must have at least invno or dpono
    if (!row.invno && !row.dpono) {
      toast.error("This row has no invno and no dpono");
      return;
    }

    setActiveRow(row);
    setDrawerOpen(true);
    setDrawerTab(0);

    setItems([]);
    setCases([]);

    setItemsLoading(true);
    setCasesLoading(!!row.invno);

    try {
      const calls = [];

      if (row.invno) {
        calls.push(fetchJSON(`/dpo/dashboard/invoice/${encodeURIComponent(row.invno)}/items`));
        calls.push(
          fetchJSON(
            `/dpo/dashboard/invoice/${encodeURIComponent(row.invno)}/cases?rackMissing=${rackMissing ? "YES" : "NO"}`
          )
        );
      } else {
        calls.push(fetchJSON(`/dpo/dashboard/dpo/${encodeURIComponent(row.dpono)}/items`));
      }

      const results = await Promise.all(calls);

      if (row.invno) {
        setItems(results[0]?.rows || []);
        setCases(results[1]?.rows || []);
      } else {
        setItems(results[0]?.rows || []);
        setCases([]);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setItemsLoading(false);
      setCasesLoading(false);
    }
  };

  useEffect(() => {
    const reloadCases = async () => {
      if (!activeRow?.invno) return;
      setCasesLoading(true);
      try {
        const c = await fetchJSON(
          `/dpo/dashboard/invoice/${encodeURIComponent(activeRow.invno)}/cases?rackMissing=${rackMissing ? "YES" : "NO"}`
        );
        setCases(c.rows || []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setCasesLoading(false);
      }
    };
    reloadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rackMissing]);

  const cols = [
    {
      field: "dpono",
      headerName: "DPO",
      minWidth: 250,
      flex: 0.7,
      renderCell: (p) => (p.value ? <Chip size="small" label={p.value} /> : <Chip size="small" label="-" />),
    },
    {
      field: "invno",
      headerName: "Invoice",
      minWidth: 170,
      flex: 0.9,
      renderCell: (p) =>
        p.value ? (
          <Button size="small" onClick={() => openRow(p.row)}>
            {p.value}
          </Button>
        ) : (
          <Chip size="small" label="NO INV" />
        ),
    },
    {
      field: "subledgername",
      headerName: "Party",
      minWidth: 500,
      flex: 1,
      valueGetter: (p) => p.row?.subledgername || (p.row?.subledgerid ? `#${p.row.subledgerid}` : "-"),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 0.8,
      renderCell: (p) => <StatusChip v={p.value} />,
    },
    { field: "dpodate", headerName: "DPO Dt", minWidth: 95, valueFormatter: (p) => fmt(p.value) },
    { field: "expected_qty", headerName: "DPOQty", minWidth: 90, type: "number" },
    { field: "transit_qty", headerName: "TRQty", minWidth: 90, type: "number" },
    { field: "case_qty", headerName: "CSQty", minWidth: 90, type: "number" },
    {
      field: "purchased",
      headerName: "Pur",
      minWidth: 70,
      sortable: true,
      renderCell: (p) => (p.value ? <Chip size="small" color="success" label="Y" /> : <Chip size="small" label="N" />),
    },
    { field: "pending_receive_qty", headerName: "Pending", minWidth: 95, type: "number" },

    // ✅ keep server-sort compatible (hidden)
    {
      field: "last_activity",
      headerName: "Last",
      minWidth: 95,
      hide: true,
      valueGetter: (p) => p.row?.last_activity_date || null,
      valueFormatter: (p) => fmt(p.value),
    },
  ];

  if (!isLoggedIn) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2000} newestOnTop />

      <Sidebar
        Title={"DPO Status"}
        expandedPanel={"panel2"}
        children={
          <Box sx={{ p: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                sx={{ py: 1, "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 700 } }}
                title="DPO / Invoice Status"
                subheader={<Typography variant="caption">Search by DPO or Invoice • Double click row to open</Typography>}
              />
              <Divider />
              <CardContent sx={{ p: 1 }}>
                {/* Filters (compact) */}
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <TextField size="small" select fullWidth label="Date Mode" value={dateMode} onChange={(e) => setDateMode(e.target.value)}>
                      <MenuItem value="transit">Transit</MenuItem>
                      <MenuItem value="dpo">DPO</MenuItem>
                      <MenuItem value="case">Case</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField size="small" fullWidth label="From" type="date" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField size="small" fullWidth label="To" type="date" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      size="small"
                      open={partyOpen}
                      onOpen={() => setPartyOpen(true)}
                      onClose={() => setPartyOpen(false)}
                      options={partyOptions}
                      loading={partyLoading}
                      value={party}
                      onChange={(e, v) => setParty(v)}
                      getOptionLabel={(o) => (o ? `${o.subledgername || ""} (${o.subledgercode || ""})` : "")}
                      renderInput={(params) => (
                        <TextField {...params} size="small" fullWidth label="Party" onChange={(e) => setPartyQ(e.target.value)} />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField size="small" fullWidth label="Search (DPO/Invoice)" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField size="small" select fullWidth label="NoInv" value={showNoInv} onChange={(e) => setShowNoInv(e.target.value)}>
                      <MenuItem value="YES">Show</MenuItem>
                      <MenuItem value="NO">Hide</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField size="small" select fullWidth label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="NO_INVNO">NO_INVNO</MenuItem>
                      <MenuItem value="DPO_ONLY">DPO_ONLY</MenuItem>
                      <MenuItem value="TRANSIT_ONLY">TRANSIT_ONLY</MenuItem>
                      <MenuItem value="PENDING_RECEIVE">PENDING</MenuItem>
                      <MenuItem value="SHORT_RECEIVE">SHORT</MenuItem>
                      <MenuItem value="EXCESS_RECEIVE">EXCESS</MenuItem>
                      <MenuItem value="MATCHED_OK">OK</MenuItem>
                      <MenuItem value="CHECK">CHECK</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField size="small" select fullWidth label="Purchased" value={purchased} onChange={(e) => setPurchased(e.target.value)}>
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="YES">YES</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button size="small" variant="contained" onClick={apply} disabled={gridLoading}>
                        Apply
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setDateMode("transit");
                          setFrom(daysAgoISO(30));
                          setTo(todayISO());
                          setParty(null);
                          setPartyQ("");
                          setStatus("ALL");
                          setPurchased("ALL");
                          setSearch("");
                          setShowNoInv("YES");
                          setPage(0);
                        }}
                      >
                        Reset
                      </Button>
                      {gridLoading && <CircularProgress size={18} />}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                {/* Grid */}
                <Box sx={{ height: 580 }}>
                  <DataGrid
                    rows={rows}
                    columns={cols}
                    getRowId={(r) => r.id || `${r.invno || "NOINV"}-${r.dpono || "NODPO"}-${r.subledgerid || 0}`}
                    density="compact"
                    rowHeight={30}
                    loading={gridLoading}
                    rowCount={rowCount}
                    pagination
                    paginationMode="server"
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(p) => setPage(p)}
                    onPageSizeChange={(s) => {
                      setPageSize(s);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(m) => setSortModel(m)}
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
                    onRowDoubleClick={(p) => openRow(p.row)}
                  />
                </Box>

                {/* Drawer */}
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { width: { xs: "100%", md: 860 } } }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Box>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          {activeRow?.dpono ? `DPO: ${activeRow.dpono}` : "DPO: -"}{" "}
                          {activeRow?.invno ? ` | INV: ${activeRow.invno}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activeRow?.subledgername || (activeRow?.subledgerid ? `Party #${activeRow.subledgerid}` : "")}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {activeRow?.status && <StatusChip v={activeRow.status} />}
                        {activeRow?.invno && (
                          <Button size="small" variant={rackMissing ? "contained" : "outlined"} onClick={() => setRackMissing((s) => !s)}>
                            Rack Missing
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    {/* Summary chips from the grid row itself */}
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="small" label={`DPO Dt: ${fmt(activeRow?.dpodate) || "-"}`} />
                      <Chip size="small" label={`Inv Dt: ${fmt(activeRow?.invdate) || "-"}`} />
                      <Chip size="small" label={`Last CS: ${fmt(activeRow?.last_case_date) || "-"}`} />
                      <Chip size="small" label={`DPOQty: ${num(activeRow?.expected_qty)}`} />
                      <Chip size="small" label={`TRQty: ${num(activeRow?.transit_qty)}`} />
                      <Chip size="small" label={`CSQty: ${num(activeRow?.case_qty)}`} />
                      <Chip size="small" label={`Pending: ${num(activeRow?.pending_receive_qty)}`} />
                      <Chip size="small" label={`Purchased: ${activeRow?.purchased ? "YES" : "NO"}`} />
                    </Stack>

                    <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)} sx={{ mt: 1 }}>
                      <Tab label="Items" />
                      {activeRow?.invno && <Tab label="Casefile" />}
                    </Tabs>

                    {/* Items */}
                    {drawerTab === 0 && (
                      <Box sx={{ mt: 1, height: 480 }}>
                        {itemsLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DataGrid
                            rows={items}
                            getRowId={(r) => r.partno || `${r.itemname}-${Math.random()}`}
                            columns={[
                              { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
                              { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.2 },
                              { field: "dpo_qty", headerName: "DPO", width: 90, type: "number" },
                              { field: "transit_qty", headerName: "TR", width: 90, type: "number" },
                              { field: "case_qty", headerName: "CS", width: 90, type: "number" },
                              { field: "pending_receive_qty", headerName: "Pending", width: 95, type: "number" },
                              { field: "diff_transit_vs_dpo", headerName: "TR-DPO", width: 95, type: "number" },
                              { field: "diff_case_vs_transit", headerName: "CS-TR", width: 95, type: "number" },
                              { field: "transit_value", headerName: "Value", width: 110, type: "number" },
                            ]}
                            density="compact"
                            rowHeight={30}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Casefile */}
                    {activeRow?.invno && drawerTab === 1 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {casesLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DataGrid
                            rows={cases}
                            getRowId={(r) => r.caseid}
                            columns={[
                              { field: "casedate", headerName: "Date", width: 100, valueFormatter: (p) => fmt(p.value) },
                              { field: "dealername", headerName: "Dealer", minWidth: 170, flex: 1 },
                              { field: "city", headerName: "City", width: 110 },
                              { field: "casenumber", headerName: "Case", width: 130 },
                              { field: "partno", headerName: "PartNo", width: 140 },
                              { field: "qty", headerName: "Qty", width: 80, type: "number" },
                              { field: "rackid", headerName: "Rack", width: 90 },
                            ]}
                            density="compact"
                            rowHeight={30}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Drawer>
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default DpoDashboard;
